/**
 * Synchronous snapshot of "where in the app are they, on what device" —
 * collected at the moment a bug report is filed. Lightweight, no React deps,
 * safe to call from event handlers.
 */

import { APP_BUILD_INFO } from './appBuildInfo';

export interface AppStateSnapshot {
  route: string;
  search: string;
  buildId: string;
  /** Short git SHA of the running bundle, when the build recorded one. */
  buildSha: string | null;
  /** When the running bundle was built (ISO), when the build recorded it. */
  builtAt: string;
  viewport: { width: number; height: number; dpr: number };
  device: { isTouch: boolean; platform: string; language: string };
  theme: 'light' | 'dark' | 'unknown';
  user?: {
    id?: string;
    email?: string;
    activeWorkspaceId?: string;
    role?: string;
  };
  online: boolean;
  capturedAt: string;
}

// `auth_user` is the canonical key AuthContext persists the signed-in user
// under; the others are legacy fallbacks. Without `auth_user` here the
// snapshot silently dropped every user/workspace field from bug reports.
const STORED_USER_KEYS = ['auth_user', 'quantis-user', 'currentUser', 'user'];

function readStoredUser(): Record<string, unknown> | null {
  if (typeof localStorage === 'undefined') return null;
  for (const key of STORED_USER_KEYS) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed as Record<string, unknown>;
    } catch {
      // ignore — try next key
    }
  }
  return null;
}

function readTheme(): 'light' | 'dark' | 'unknown' {
  if (typeof document === 'undefined') return 'unknown';
  if (document.documentElement.classList.contains('dark')) return 'dark';
  if (document.documentElement.classList.contains('light')) return 'light';
  try {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  } catch {
    // ignore
  }
  return 'light';
}

export function captureAppState(): AppStateSnapshot {
  // __APP_BUILD_INFO__ is a Vite `define` -- a textual substitution of the
  // bare identifier at build time, NOT a runtime global. Reading it off
  // globalThis therefore never matched anything, so this returned 'n/a' on
  // every bug report ever filed (#1006), including the ones where knowing
  // the build would have closed the issue in seconds (#909). appBuildInfo.ts
  // consumes the identifier the way the define expects; go through it.
  const buildId = APP_BUILD_INFO?.buildId || 'n/a';

  const user = readStoredUser();

  return {
    route: typeof window !== 'undefined' ? window.location.pathname : 'n/a',
    search: typeof window !== 'undefined' ? window.location.search : '',
    buildId,
    buildSha: APP_BUILD_INFO?.shortGitSha ?? null,
    builtAt: APP_BUILD_INFO?.builtAt || '',
    viewport: {
      width: typeof window !== 'undefined' ? window.innerWidth : 0,
      height: typeof window !== 'undefined' ? window.innerHeight : 0,
      dpr: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
    },
    device: {
      isTouch: typeof window !== 'undefined' && 'ontouchstart' in window,
      platform: typeof navigator !== 'undefined' ? navigator.platform : 'n/a',
      language: typeof navigator !== 'undefined' ? navigator.language : 'n/a',
    },
    theme: readTheme(),
    user: user
      ? {
          id: typeof user.id === 'string' ? user.id : undefined,
          email: typeof user.email === 'string' ? user.email : undefined,
          activeWorkspaceId: typeof user.activeWorkspaceId === 'string'
            ? user.activeWorkspaceId
            : typeof user.active_workspace_id === 'string'
              ? (user.active_workspace_id as string)
              : typeof user.companyId === 'string'
                ? (user.companyId as string)
                : typeof user.company_id === 'string'
                  ? (user.company_id as string)
                  : undefined,
          role: typeof user.role === 'string'
            ? user.role
            : typeof user.workspace_role === 'string'
              ? (user.workspace_role as string)
              : undefined,
        }
      : undefined,
    online: typeof navigator !== 'undefined' ? navigator.onLine : true,
    capturedAt: new Date().toISOString(),
  };
}

/** Markdown table for the GitHub issue body. */
/** Names the running bundle so a report can be checked against what was
  * deployed at the time -- a stale tab is otherwise invisible in the report. */
function formatBuild(state: AppStateSnapshot): string {
  if (state.buildId === 'n/a' && !state.buildSha) return 'n/a';
  const head = state.buildSha ? `${state.buildId} (${state.buildSha})` : state.buildId;
  return state.builtAt ? `${head} · built ${state.builtAt}` : head;
}

export function appStateAsMarkdown(state: AppStateSnapshot): string {
  const rows: Array<[string, string]> = [
    ['Route', `\`${state.route}${state.search}\``],
    ['Build', formatBuild(state)],
    ['Viewport', `${state.viewport.width}×${state.viewport.height} @ ${state.viewport.dpr}x`],
    ['Device', `${state.device.platform}${state.device.isTouch ? ' (touch)' : ''} • ${state.device.language}`],
    ['Theme', state.theme],
    ['Online', state.online ? 'yes' : 'no'],
  ];
  if (state.user?.email) rows.push(['User', state.user.email]);
  if (state.user?.activeWorkspaceId) rows.push(['Workspace', state.user.activeWorkspaceId]);
  if (state.user?.role) rows.push(['Role', state.user.role]);

  return [
    '| Field | Value |',
    '|-------|-------|',
    ...rows.map(([k, v]) => `| ${k} | ${v} |`),
  ].join('\n');
}
